import { useAuth } from '../context/AuthContext';
import { useMemo } from 'react';

/**
 * Permission levels and badge-based access control
 * 
 * Access hierarchy:
 * - admin (role): Full platform access
 * - mayor (badge): All wards, councillor features + city-wide
 * - councillor (badge): Own ward only
 * - former-councillor (badge): Own former ward, read-only
 * - community-founder (badge): Special community features
 * - user (default): Standard resident access
 */

// Feature permissions mapped to badges/roles
const PERMISSIONS = {
  // Admin features
  'admin.users': ['admin'],
  'admin.moderation': ['admin', 'community_leader', 'community-leader', 'community_leader'],
  'admin.reports': ['admin', 'community_leader', 'community-leader', 'community_leader'],
  'admin.councillors': ['admin'],
  'admin.votes': ['admin'],
  'admin.ads': ['admin'],
  'admin.settings': ['admin'],
  
  // Councillor features
  'councillor.dashboard': ['mayor', 'councillor'],
  'councillor.send_blast': ['mayor', 'councillor'],
  'councillor.view_messages': ['mayor', 'councillor'],
  'councillor.respond': ['mayor', 'councillor'],
  'councillor.view_constituents': ['mayor', 'councillor'],
  
  // Former official features (read-only)
  'former.view_history': ['former-mayor', 'former-councillor'],
  
  // Community features
  'community.founders_area': ['community-founder'],
  'community.early_access': ['community-founder', 'mayor', 'councillor'],
};

// Badges that grant all-ward access
const ALL_WARD_ACCESS = ['mayor', 'admin'];

// Badges that grant ward-specific access
const WARD_SPECIFIC_ACCESS = ['councillor', 'former-councillor'];

export const usePermissions = () => {
  const { user, isAdmin } = useAuth();

  const permissions = useMemo(() => {
    if (!user) {
      return {
        badges: [],
        role: null,
        ward: null,
        councillorWard: null,
        isAdmin: false,
        isMayor: false,
        isCouncillor: false,
        isFormerCouncillor: false,
        isFormerMayor: false,
        isCommunityFounder: false,
        isModerator: false,
      };
    }

    // Extract badge codes from user data
    const badges = (user.user_badges || user.badges || [])
      .map(b => b.code || b.badge_code)
      .filter(Boolean);

    return {
      badges,
      role: user.role,
      ward: user.ward,
      councillorWard: user.councillor_ward || user.ward, // Ward they represent as councillor
      isAdmin: user.role === 'admin',
      isMayor: badges.includes('mayor'),
      isCouncillor: badges.includes('councillor'),
      isFormerCouncillor: badges.includes('former-councillor'),
      isFormerMayor: badges.includes('former-mayor'),
      isCommunityFounder: badges.includes('community-founder'),
      isModerator: user.role === 'community_leader' || badges.includes('community-leader') || badges.includes('community_leader'),
    };
  }, [user]);

  /**
   * Check if user has a specific badge
   */
  const hasBadge = (badgeCode) => {
    return permissions.badges.includes(badgeCode);
  };

  /**
   * Check if user can perform a specific action
   * @param {string} permission - Permission key from PERMISSIONS object
   */
  const can = (permission) => {
    // Admins can do everything
    if (permissions.isAdmin) return true;

    const allowedBadgesOrRoles = PERMISSIONS[permission];
    if (!allowedBadgesOrRoles) return false;

    // Check if user's role matches
    if (allowedBadgesOrRoles.includes(permissions.role)) return true;

    // Check if user has any of the required badges
    return allowedBadgesOrRoles.some(badge => permissions.badges.includes(badge));
  };

  /**
   * Check if user can access a specific ward's data
   * @param {string} ward - Ward to check access for (e.g., "Ward 3")
   */
  const canAccessWard = (ward) => {
    // Admins and mayors can access all wards
    if (permissions.isAdmin || permissions.isMayor) return true;

    // Councillors can only access their own ward
    if (permissions.isCouncillor) {
      return permissions.councillorWard === ward;
    }

    // Former councillors can view their former ward (read-only enforced elsewhere)
    if (permissions.isFormerCouncillor) {
      return permissions.councillorWard === ward;
    }

    return false;
  };

  /**
   * Check if user has read-only access (former officials)
   */
  const isReadOnly = () => {
    // Former officials have read-only access
    if (permissions.isFormerCouncillor || permissions.isFormerMayor) {
      // Unless they're also current officials
      if (!permissions.isCouncillor && !permissions.isMayor && !permissions.isAdmin) {
        return true;
      }
    }
    return false;
  };

  /**
   * Get list of wards user can access
   * Returns null for all-ward access, or array of specific wards
   */
  const getAccessibleWards = () => {
    if (permissions.isAdmin || permissions.isMayor) {
      return null; // null means all wards
    }

    if (permissions.isCouncillor || permissions.isFormerCouncillor) {
      return permissions.councillorWard ? [permissions.councillorWard] : [];
    }

    return []; // No councillor ward access
  };

  /**
   * Get the user's civic title for display
   */
  const getCivicTitle = () => {
    if (permissions.isMayor) return 'Mayor';
    if (permissions.isCouncillor) return `Councillor - ${permissions.councillorWard || 'Unassigned'}`;
    if (permissions.isFormerMayor) return 'Former Mayor';
    if (permissions.isFormerCouncillor) return `Former Councillor - ${permissions.councillorWard || ''}`;
    return null;
  };

  return {
    ...permissions,
    hasBadge,
    can,
    canAccessWard,
    isReadOnly,
    getAccessibleWards,
    getCivicTitle,
    PERMISSIONS, // Export for reference
  };
};

export default usePermissions;
